import { X, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

interface PersonEnrichment {
    type: string;
    status: string;
    data?: any;
    options?: any[];
    lastRunAt?: string;
    error?: string;
}

interface PersonDoc {
    _id: string;
    name: string;
    role?: string;
    relevanceScore?: number;
    enrichments: PersonEnrichment[];
    createdAt?: string;
    establishments?: Array<{ _id: string; name: string }>;
}

function getStatusColor(status: string): string {
    switch (status) {
        case "done":
        case "approved":
            return "bg-green-50 text-green-800 border-green-200";
        case "pending":
        case "waiting_callback":
            return "bg-yellow-50 text-yellow-800 border-yellow-200";
        case "error":
            return "bg-red-50 text-red-800 border-red-200";
        default:
            return "bg-gray-50 text-gray-800 border-gray-200";
    }
}

function formatDate(dateString?: string): string {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function PersonDetailModal({ personId, onClose }: { personId: string; onClose: () => void }) {
    const [person, setPerson] = useState<PersonDoc | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showingOptions, setShowingOptions] = useState<number | null>(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(`${API_URL}/admin/people/${personId}`, { headers: authHeaders() });
            setPerson(data.data);
        } catch (err) {
            console.error("Failed to load person:", err);
            setError("Impossible de charger cette personne");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personId]);

    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-navy-700">Détail personne</h2>
                        <p className="text-xs text-gray-600 mt-0.5">{person?.name ?? "…"}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <p className="text-sm text-gray-400">Chargement…</p>
                    ) : error ? (
                        <p className="text-sm text-red-600">{error}</p>
                    ) : person ? (
                        <>
                            <div className="mb-6">
                                <h3 className="text-sm font-semibold text-navy-700 mb-3">Overview</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Nom</label>
                                        <p className="text-sm text-gray-900">{person.name}</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Rôle</label>
                                        <p className="text-sm text-gray-900">{person.role || "N/A"}</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Score</label>
                                        <p className="text-sm text-gray-900">{person.relevanceScore ?? "N/A"}</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Créé le</label>
                                        <p className="text-sm text-gray-900">{formatDate(person.createdAt)}</p>
                                    </div>
                                </div>
                                {person.establishments && person.establishments.length > 0 && (
                                    <div className="mt-3">
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Établissement(s) lié(s)</label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {person.establishments.map((e) => (
                                                <a
                                                    key={e._id}
                                                    href={`/establishments/${e._id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs px-2 py-0.5 bg-gray-100 rounded-sm hover:bg-gray-200 text-gray-700"
                                                >
                                                    {e.name}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <h3 className="text-sm font-semibold text-navy-700 mb-3">Enrichissements</h3>
                                <div className="bg-white border border-gray-200 rounded-xs overflow-hidden">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Type</th>
                                                <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Statut</th>
                                                <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Dernière exécution</th>
                                                <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Données</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {(person.enrichments ?? []).map((enr, i) => (
                                                <tr key={i} className="hover:bg-[#F9F6F3]">
                                                    <td className="px-3 py-2 text-xs font-medium text-gray-900">{enr.type}</td>
                                                    <td className="px-3 py-2">
                                                        <span className={`px-2 py-0.5 text-[11px] font-medium rounded-sm border ${getStatusColor(enr.status)}`}>
                                                            {enr.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-xs text-gray-600">
                                                        {enr.lastRunAt ? formatDate(enr.lastRunAt) : "Jamais"}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {showingOptions === i ? (
                                                            <div className="flex flex-col gap-2 max-w-md">
                                                                <div className="text-[10px] font-semibold text-gray-700">
                                                                    {enr.options?.length ?? 0} option(s) :
                                                                </div>
                                                                <div className="space-y-1 max-h-60 overflow-y-auto">
                                                                    {(enr.options ?? []).map((opt, optIndex) => (
                                                                        <pre
                                                                            key={optIndex}
                                                                            className="text-[10px] bg-gray-50 border border-gray-200 rounded p-1.5 whitespace-pre-wrap"
                                                                        >
                                                                            {JSON.stringify(opt, null, 2)}
                                                                        </pre>
                                                                    ))}
                                                                </div>
                                                                <button
                                                                    onClick={() => setShowingOptions(null)}
                                                                    className="px-2 py-1 text-[10px] bg-gray-600 text-white rounded hover:bg-gray-700 self-start"
                                                                >
                                                                    Fermer
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col gap-2">
                                                                {enr.data ? (
                                                                    <pre className="text-[10px] bg-gray-100 p-2 rounded max-w-xs overflow-auto">
                                                                        {JSON.stringify(enr.data, null, 2)}
                                                                    </pre>
                                                                ) : (
                                                                    <span className="text-xs text-gray-400">Pas de données</span>
                                                                )}
                                                                {enr.options && enr.options.length > 0 && (
                                                                    <button
                                                                        onClick={() => setShowingOptions(i)}
                                                                        className="px-2 py-1 text-[10px] bg-gray-100 text-gray-700 rounded hover:bg-gray-200 self-start"
                                                                    >
                                                                        Voir {enr.options.length} option{enr.options.length > 1 ? "s" : ""}
                                                                    </button>
                                                                )}
                                                                {enr.error && <p className="text-xs text-red-600 mt-1">{enr.error}</p>}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <button
                        onClick={load}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-xs hover:bg-[#F9F6F3] transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Rafraîchir
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-xs bg-black text-white rounded-xs hover:bg-gray-800 transition-colors"
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
